import {useMediaQuery} from '@chakra-ui/react';
import {unwrapResult} from '@reduxjs/toolkit';
import React, {useState} from 'react';
import {CalendarContainer} from 'react-datepicker';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {requestOK} from 'redux-thunk-kit';
import {
  AppDatePicker,
  Col,
  Row,
  UiBox,
  UiButton,
  UiQuestionnaireLayout,
  UiSimpleGrid,
  UiText,
} from '~/components/elements';
import {MultipleChipSelect} from '~/components/shared/MultipleChipSelect';
import {PAGE_URLS, QUERY_MOBILE} from '~/constants';
import {WEIGHT_LOSS_KEYS, WEIGHT_LOSS_ONE_QUESTIONNAIRES} from '~/constants/medical-questionnaires';
import {useInput, useRequestState} from '~/hooks';
import {setMedicalUUID, setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {submitOrLogMdq} from '~/redux/questionnaires/thunk';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {Option} from '~/types/models/questionnaire';
import {DATE_FORMAT, redirectInIframe} from '~/utils/helpers';
import {addBusinessDays, isWeekend} from 'date-fns';
import moment from 'moment';

interface Props {}

const QuestionnaireNutritionDeliveryScreen = ({}: Props) => {
  const dispatch: AppDispatch = useDispatch();
  const history = useHistory();
  const mdq = useSelector((state: RootState) => state.questionnaires);
  const {methodKey, medicalUUID, answerOrder} = mdq;

  const [redirectLoading, setRedirectLoading] = useState(false);
  const {loading} = useRequestState(submitOrLogMdq);

  //   const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);
  //   const {wpAuthUser} = medicalQuestionnaire;

  //   const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = WEIGHT_LOSS_ONE_QUESTIONNAIRES.find(i => i.key === WEIGHT_LOSS_KEYS.MEAL_DELIVERY_TIME);

  const currentAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectedData[WEIGHT_LOSS_KEYS.MEAL_DELIVERY_TIME]?.data,
  );

  const collectedData = mdq.collectedData;

  const {inputs, setInputs, validation} = useInput({
    selectedDay: currentAnswer?.selectedDay ? moment(currentAnswer.selectedDay).toDate() : '',
    selectedTime: currentAnswer?.selectedTime || '',
  });

  const onSelectDay = (selectedDay: Date) => {
    setInputs({selectedDay});
  };

  const onSelectTime = (selectedTime: string | number) => {
    setInputs({selectedTime});
  };

  const calendarMaxDate = moment().endOf('month').add(1, 'months').toDate();

  const isPublicHoliday = (date: Date) => {
    const publicHolidays = [
      '2024-01-01', // New Year’s Day
      '2024-02-10', // Chinese New Year
      '2024-02-11', // Chinese New Year
      '2024-03-29', // Good Friday
      '2024-04-10', // Hari Raya Puasa
      '2024-05-01', // Labour Day
      '2024-05-22', // Vesak Day
      '2024-06-17', // Hari Raya Haji
      '2024-08-09', // National Day
      '2024-10-31', // Deepavali
      '2024-12-25', // Christmas Day
    ];

    const formattedDate = moment(date).format('YYYY-MM-DD');
    return publicHolidays.includes(formattedDate);
  };

  const filterDate = (date: Date) => {
    //     DATE is blocked IF:
    // Date is earlier than TODAY + 5 Business Days
    // Date is Sunday
    // Date is Saturday
    // Date is a Public Holiday

    const today = new Date();
    const day = new Date(date);
    const fiveBusinessDaysFromNow = addBusinessDays(today, 5);

    if (day < fiveBusinessDaysFromNow) {
      return false;
    }

    if (isPublicHoliday(date)) {
      return false;
    }

    return !isWeekend(day);
  };

  const onClickSubmit = async () => {
    const answerData = {
      key: WEIGHT_LOSS_KEYS.MEAL_DELIVERY_TIME,
      index: questionnaire?.index || '',
      data: {
        selectedDay: moment(inputs.selectedDay).format(DATE_FORMAT),
        selectedTime: inputs.selectedTime,
      },
      order: answerOrder + 1,
    };

    dispatch(setQuestionnaireAnswer(answerData));
    dispatch(
      submitOrLogMdq({
        data: {
          uid: medicalUUID,
          methodKey: methodKey.toLowerCase(),
          status: 'draft',
          data: {
            ...collectedData,
            [WEIGHT_LOSS_KEYS.MEAL_DELIVERY_TIME]: answerData,
          },
        },
      }),
    );
    history.push(PAGE_URLS.MDQ1_DIETICIAN_APPOINTMENT_PREFERENCES);
  };

  return (
    <UiQuestionnaireLayout
      actions={questionnaire?.actions}
      checkoutStep={questionnaire?.checkoutStep}
      handleOnNext={onClickSubmit}
      pageTitle={questionnaire?.title}
      disableNext={!inputs.selectedDay || !inputs.selectedTime || redirectLoading || loading}
    >
      <Col alignItems="center" textAlign="center" mt={12} px={2}>
        <UiText fontSize="xl" fontWeight="bold" mb={12}>
          {questionnaire?.questions?.default}
        </UiText>
        <UiText fontSize="sm" fontWeight="600" mb={4} maxW="80%">
          {questionnaire?.questions?.description}
        </UiText>
        <Col boxShadow="0px 16px 32px 0px #2C41421A" py={4} px={0}>
          <UiText fontWeight="600" fontSize="md" fontFamily="Inter" color="black" mt={4}>
            Calendar
          </UiText>
          <AppDatePicker
            inline
            startDate={undefined}
            modalContentProps={{marginTop: 'auto', marginBottom: 10}}
            minDate={new Date()}
            maxDate={calendarMaxDate}
            todayButton={undefined}
            // calendarContainer={CustomCalendarContainer}
            selected={inputs.selectedDay}
            onChange={onSelectDay}
            filterDate={filterDate}
            disabledKeyboardNavigation
          />
          <UiText fontWeight="600" fontSize="sm" fontFamily="Inter" color="black" mt={4}>
            Preferred delivery time
          </UiText>
          <Row justify="center" flexWrap="wrap" w="100%" mt={4} mb={4}>
            {questionnaire?.options?.map((time: Option, index) => (
              <UiButton
                onClick={() => onSelectTime(time.value)}
                key={time.label}
                title={time.label}
                variant={time.value === inputs.selectedTime ? 'solid' : 'outline'}
                mx={2}
                w={{base: '85px'}}
                mb={5}
                minW="48px"
                borderRadius="1.25rem"
                minH={9}
                fontSize="xs"
                fontWeight="600"
                color="trueBlack"
                borderColor="trueBlack"
                _hover={{}}
              />
            ))}
          </Row>
        </Col>
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default QuestionnaireNutritionDeliveryScreen;
